// Plan 24c, Task 4 — Minigame Countdown screen (Screen 21)
//
// Full-viewport overlay (no dim — arena remains visible) that mounts the
// countdown component over the minigame arena, then navigates to
// 'minigame-hud' when the countdown completes.
//
// Player name pills float at normalized viewport positions (x, y in [0-1])
// so the game server can position them near each player's spawn point.
//
// ESC key → return to warmup.

import { createCountdown } from '../components/countdown';
import { SHAPE_GLYPH_FOR_COLOR, colorIdFromIndex } from './shape-palette';
import type { ScreenHandle, ScreenMountContext } from './types';
import type { BoardMountPayload } from './board-payload';

// ─────────────────────────────────────────────────────────────────────────────
// Public types
// ─────────────────────────────────────────────────────────────────────────────

export type CountdownPlayerLabel = {
  playerId: string;
  name: string;
  x: number; // normalized 0-1 viewport coordinate
  y: number; // normalized 0-1 viewport coordinate
};

export type MinigameCountdownPayload = BoardMountPayload & {
  playerLabels: CountdownPlayerLabel[]; // 2-8 entries
  onComplete?: () => void; // default: router.navigate('minigame-hud', payload)
};

// ─────────────────────────────────────────────────────────────────────────────
// Design token constants — all values from docs/design/tokens.json
// ─────────────────────────────────────────────────────────────────────────────

/** z-index.overlay = 350 */
const Z_INDEX_OVERLAY = 350;

/** Player colors — index 0-9. Matches scoreboard.ts / round-summary.ts. */
const PLAYER_COLORS = [
  '#E04040', // 0 red
  '#4080D0', // 1 blue
  '#40B040', // 2 green
  '#E8D020', // 3 yellow
  '#E08820', // 4 orange
  '#9040C0', // 5 purple
  '#E06090', // 6 pink
  '#40B0C0', // 7 cyan
  '#E8E8E8', // 8 white
  '#404040', // 9 black
];

/** color.text.primary = #FFFFFF */
const COLOR_WHITE = '#FFFFFF';

/** typography.size.label = 12px */
const LABEL_FONT_SIZE = '12px';

/** border-radius.pill = 9999px */
const PILL_RADIUS = '9999px';

// ─────────────────────────────────────────────────────────────────────────────
// Factory
// ─────────────────────────────────────────────────────────────────────────────

export function createMinigameCountdownScreen(
  ctx: ScreenMountContext<MinigameCountdownPayload>,
): ScreenHandle {
  const { gsapRegistry, host, router, payload } = ctx;
  const { playerLabels } = payload;

  let disposed = false;

  // ── Root overlay ──────────────────────────────────────────────────────────
  // No background dim — the minigame arena should remain visible through this
  // overlay. pointer-events: none so input passes through to the game.

  const root = document.createElement('div');
  root.className = 'screen-minigame-countdown';
  root.setAttribute('data-screen-id', 'minigame-countdown');
  root.style.cssText = [
    'position: fixed',
    'inset: 0',
    `z-index: ${Z_INDEX_OVERLAY}`,
    'pointer-events: none',
  ].join('; ');

  // ── Countdown component ───────────────────────────────────────────────────

  const countdownHandle = createCountdown({
    gsapRegistry,
    mount: root,
    onStep: (step) => {
      payload.playSfx?.(step === 'GO!' ? 'sfx-countdown-go' : `sfx-countdown-${step}`);
    },
    onComplete: () => {
      if (disposed) return;
      if (payload.onComplete) {
        payload.onComplete();
      } else {
        router.navigate('minigame-hud', payload);
      }
    },
  });

  // Fire-and-forget — navigation handled by onComplete callback above.
  countdownHandle.start();

  // ── Player name pills ─────────────────────────────────────────────────────

  for (const label of playerLabels) {
    const player = payload.state.players.get(label.playerId);

    const pillBg = player != null ? (PLAYER_COLORS[player.color % 10] ?? '#808080') : '#808080';

    const pill = document.createElement('div');
    pill.className = 'minigame-countdown__name-pill';
    pill.setAttribute('data-player-id', label.playerId);
    pill.style.cssText = [
      'position: absolute',
      `left: ${label.x * 100}%`,
      `top: ${label.y * 100}%`,
      'transform: translate(-50%, -50%)',
      `background: ${pillBg}`,
      `color: ${COLOR_WHITE}`,
      `font-size: ${LABEL_FONT_SIZE}`,
      `border-radius: ${PILL_RADIUS}`,
      'padding: 4px 8px',
      'white-space: nowrap',
      'pointer-events: none',
    ].join('; ');

    // Player name text
    pill.textContent = label.name;

    // Colorblind shape glyph (appended as a <span> if available)
    if (player != null) {
      const colorId = colorIdFromIndex(player.color % 10);
      const glyph = colorId != null ? (SHAPE_GLYPH_FOR_COLOR[colorId] ?? '') : '';
      if (glyph) {
        const glyphSpan = document.createElement('span');
        glyphSpan.textContent = ` ${glyph}`;
        pill.appendChild(glyphSpan);
      }
    }

    root.appendChild(pill);
  }

  // ── Append root to host ───────────────────────────────────────────────────

  host.appendChild(root);

  // ── ESC key handler ───────────────────────────────────────────────────────

  function handleEsc(e: KeyboardEvent): void {
    if (e.key !== 'Escape') return;
    router.navigate('warmup-arena-overlay', { portal: null });
  }

  document.addEventListener('keydown', handleEsc);

  // ── dispose ───────────────────────────────────────────────────────────────

  return {
    element: root,
    dispose() {
      if (disposed) return;
      disposed = true;

      countdownHandle.dispose();
      document.removeEventListener('keydown', handleEsc);
      if (root.parentNode) root.parentNode.removeChild(root);
    },
  };
}
